import { Link } from "react-router-dom";
import { ArrowRight, HeartHandshake, Ear, Footprints, GraduationCap, Users, HeartPulse, ShieldCheck, PawPrint, Sparkles } from "lucide-react";
import { PageHero, Reveal } from "@/components/site/Motion";

const IMG = {
  hero: "/photos/sophie-portrait.webp",
  training: "https://images.unsplash.com/photo-1704745348211-c4311c97d9c4?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MDV8MHwxfHNlYXJjaHwyfHxkb2clMjB0cmFpbmVyJTIwZ2VudGxlfGVufDB8fHx8MTc4ODIxNTI1MHww&ixlib=rb-4.1.0&q=85&w=1200",
};

const VALUES = [
  { icon: HeartHandshake, title: "Bienveillance", text: "Aucune famille n'est jugée. Chacune est accueillie avec son histoire, ses forces et ses fatigues." },
  { icon: Ear, title: "Écoute", text: "Avant toute méthode, il y a la parole. Je prends le temps de comprendre ce qui se joue vraiment chez vous." },
  { icon: Footprints, title: "Pas à pas", text: "Le respect du rythme de chacun — humain comme animal — est au cœur de chaque séance." },
];

const PARCOURS = [
  { icon: GraduationCap, tag: "Formation", title: "Bac S, puis DUT Carrières sociales", text: "Le goût de l'humain d'abord : une formation entièrement tournée vers l'accompagnement." },
  { icon: Users, tag: "Adolescence", title: "Animatrice auprès d'adolescents", text: "Un public que je comprends profondément : la recherche identitaire, les doutes, les élans." },
  { icon: HeartPulse, tag: "Soin", title: "Ambulancière", text: "Être là, concrètement, aux côtés des personnes quand elles en ont le plus besoin." },
  { icon: ShieldCheck, tag: "Protection de l'enfance", title: "Éducatrice spécialisée", text: "3 ans au service des enfants et des familles, une pierre de plus à l'édifice." },
  { icon: PawPrint, tag: "Animal", title: "Médiation animale & éducation canine", text: "Formée à l'Institut français de zoothérapie en 2019 et titulaire de l'ACACED chien, chat, NAC (août 2026). J'ai éduqué plusieurs chiens — désormais au cœur de ma méthode." },
  { icon: Sparkles, tag: "Aujourd'hui", title: "L'effet poilus", text: "L'aboutissement de tout ce parcours : le coaching familial à travers l'animal, à votre domicile.", highlight: true },
];

export default function APropos() {
  return (
    <div data-testid="apropos-page">
      <PageHero
        testId="apropos-hero"
        overline="À propos"
        lines={[
          { text: "Derrière L'effet poilus," },
          { text: "une passionnée du prendre soin.", accent: true },
        ]}
      />

      <section className="px-6 pb-24 sm:px-10 md:pb-32 lg:px-16" data-testid="apropos-story">
        <div className="mx-auto grid max-w-7xl items-start gap-14 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <Reveal y={50}>
              <div className="relative">
                <div className="absolute -left-5 -top-5 h-full w-full blob-3 bg-terracotta-soft" aria-hidden="true" />
                <div className="blob-3 relative aspect-[4/5] overflow-hidden shadow-[0_30px_60px_-30px_rgba(43,58,48,0.4)]">
                  <img src={IMG.hero} alt="Sophie Reitzer, fondatrice de L'effet poilus, avec ses chiens" loading="lazy" className="h-full w-full object-cover transition-transform duration-700 hover:scale-105" data-testid="apropos-portrait" />
                </div>
              </div>
            </Reveal>
            <Reveal delay={0.15}>
              <p className="mt-10 border-l-2 border-terracotta pl-6 font-serif text-2xl italic leading-snug text-ink/80" data-testid="apropos-quote">
                « Un animal ne règle pas les problèmes d'une famille.
                Il lui donne envie de les traverser ensemble. »
              </p>
            </Reveal>
          </div>

          <div className="lg:col-span-6 lg:col-start-7">
            <Reveal delay={0.1}>
              <h2 className="font-serif text-3xl font-medium leading-[1.15] tracking-tight text-ink sm:text-4xl">
                Sophie Reitzer, <span className="italic text-terracotta">fondatrice de L'effet poilus</span>
              </h2>
              <p className="mt-7 max-w-md text-base leading-relaxed text-ink/70 md:text-lg">
                Chaque étape de mon parcours s'est ajoutée à la précédente —
                jamais ne l'a remplacée. C'est ce fil continu, de l'accompagnement
                des adolescents à l'éducation canine, qui a donné naissance à
                L'effet poilus.
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="px-6 py-24 sm:px-10 md:py-32 lg:px-16" data-testid="apropos-parcours">
        <div className="mx-auto max-w-7xl">
          <Reveal>
            <p className="overline-label">Mon parcours</p>
            <h2 className="mt-4 max-w-2xl font-serif text-3xl font-medium leading-[1.15] tracking-tight text-ink sm:text-4xl md:text-5xl">
              Un fil rouge : <span className="italic text-terracotta">prendre soin.</span>
            </h2>
          </Reveal>
          <div className="mt-16 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {PARCOURS.map((step, i) => (
              <Reveal key={step.title} delay={0.08 * i} y={40}>
                <div
                  className={`h-full rounded-[2rem] border p-9 transition-transform duration-300 hover:-translate-y-1.5 ${
                    step.highlight
                      ? "border-transparent bg-terracotta text-cream shadow-[0_35px_70px_-30px_rgba(200,90,50,0.55)]"
                      : "border-stone-border bg-white/70"
                  }`}
                  data-testid={`parcours-step-${i + 1}`}
                >
                  <span className={`flex h-12 w-12 items-center justify-center rounded-full ${step.highlight ? "bg-cream/15 text-cream" : "bg-terracotta/10 text-terracotta"}`}>
                    <step.icon size={20} strokeWidth={1.8} />
                  </span>
                  <p className={`mt-6 text-xs font-bold uppercase tracking-[0.2em] ${step.highlight ? "text-cream/70" : "text-terracotta"}`}>
                    {step.tag}
                  </p>
                  <h3 className={`mt-2 font-serif text-2xl font-medium ${step.highlight ? "text-cream" : "text-ink"}`}>{step.title}</h3>
                  <p className={`mt-3 text-sm leading-relaxed ${step.highlight ? "text-cream/85" : "text-ink/65"}`}>{step.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-linen px-6 py-24 sm:px-10 md:py-32 lg:px-16" data-testid="apropos-values">
        <div className="mx-auto max-w-7xl">
          <Reveal>
            <p className="overline-label">Mes valeurs</p>
            <h2 className="mt-4 max-w-xl font-serif text-3xl font-medium leading-[1.15] tracking-tight text-ink sm:text-4xl md:text-5xl">
              Ce qui guide <span className="italic text-terracotta">chaque séance.</span>
            </h2>
          </Reveal>
          <div className="mt-16 grid gap-6 md:grid-cols-3">
            {VALUES.map((v, i) => (
              <Reveal key={v.title} delay={0.1 * i} y={40}>
                <div className="h-full rounded-[2rem] border border-stone-border bg-cream p-10 transition-transform duration-300 hover:-translate-y-1.5" data-testid={`apropos-value-${i + 1}`}>
                  <span className="flex h-12 w-12 items-center justify-center rounded-full bg-terracotta/10 text-terracotta">
                    <v.icon size={20} strokeWidth={1.8} />
                  </span>
                  <h3 className="mt-6 font-serif text-2xl font-medium text-ink">{v.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-ink/65">{v.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.2}>
            <div className="mt-16 flex flex-col items-start justify-between gap-8 rounded-[2rem] bg-forest-deep p-10 md:flex-row md:items-center md:p-12">
              <p className="max-w-lg font-serif text-2xl italic leading-snug text-cream md:text-3xl">
                Envie de renouer des liens, de rétablir l'équilibre dans votre foyer ?
              </p>
              <Link to="/contact" className="btn-primary shrink-0" data-testid="apropos-cta-button">
                Me contacter <ArrowRight size={16} />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
